import type { Column } from "@shared/types/data-table"
import type { Payment } from "@shared/types/payment.types"
import type { UserRole } from "@shared/services/session.service"
import type { ColumnManagementConfig } from "@shared/lib/tables/utils/column-management"
import { transformPaymentData } from "@shared/lib/tables/transformers/payment-transformer"
import { 
  TextCell, 
  DynamicDateCell, 
  CurrencyCell,
  IconTextCell,
  TruncatedCell
} from "@shared/lib/tables/cells"
import { AppStatusBadge } from "@shared/components/ui/status-badge/AppStatusBadge"
import { CreditCard } from "lucide-react"

export const PAYMENT_COLUMNS: Record<string, Column<Payment>> = {
  amount: {
    key: "amount",
    header: "Amount",
    width: 120,
    align: "right",
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return (
        <CurrencyCell
          amount={data.amount}
          currency={data.currency}
          decimals={data.decimals}
          align="right"
          className="min-w-[68px]"
        />
      )
    },
  },
  status: {
    key: "status",
    header: "",
    width: 120,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <AppStatusBadge entityType="payment" status={data.status} />
    },
  },
  customer: {
    key: "customer",
    header: "Customer",
    width: 180,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <TextCell value={data.customer} maxWidth={180} />
    },
  },
  description: {
    key: "description",
    header: "Description",
    width: 240,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <TextCell value={data.description} maxWidth={240} />
    },
  },
  paymentMethod: {
    key: "paymentMethod",
    header: "Payment method",
    width: 160,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      if (data.isCardPayment) {
        return (
          <IconTextCell
            icon={<CreditCard className="w-3 h-3" />}
            iconClassName="text-muted-foreground"
          >
            <TruncatedCell maxWidth={160} className="text-muted-foreground">
              {data.paymentMethod}
            </TruncatedCell>
          </IconTextCell>
        )
      }
      return (
        <TruncatedCell maxWidth={160} className="text-muted-foreground">
          {data.paymentMethod}
        </TruncatedCell>
      )
    },
  },
  created: {
    key: "createdAt",
    header: "Created",
    width: 130,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <DynamicDateCell timestamp={data.createdAt} />
    },
  },
  channel: {
    key: "channel",
    header: "Channel",
    width: 200,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      const display = data.channel && data.channelId
        ? `${data.channel} (${data.channelId})`
        : data.channel
      return <TextCell value={display} maxWidth={200} />
    },
  },
  refundReason: {
    key: "refundReason",
    header: "Refund reason",
    width: 150,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <TextCell value={data.refundReason} />
    },
  },
  requested: {
    key: "requested",
    header: "Requested",
    width: 130,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <DynamicDateCell timestamp={data.requestedAt} />
    },
  },
  refunded: {
    key: "refunded",
    header: "Refunded",
    width: 130,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <DynamicDateCell timestamp={data.refundedAt} />
    },
  },
  refundedBy: {
    key: "refundedBy",
    header: "Refunded by",
    width: 150,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <TextCell value={data.refundedBy} />
    },
  },
  eligibility: {
    key: "eligibility",
    header: "Eligibility",
    width: 120,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <TextCell value={data.eligibility} />
    },
  },
  stage: {
    key: "stage",
    header: "Stage",
    width: 120,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <TextCell value={data.disputeStage} />
    },
  },
  disputeReason: {
    key: "disputeReason",
    header: "Dispute reason",
    width: 150,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <TextCell value={data.disputeReason} />
    },
  },
  disputedOn: {
    key: "disputedOn",
    header: "Disputed on",
    width: 130,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <DynamicDateCell timestamp={data.disputedOn} />
    },
  },
  winLikelihood: {
    key: "winLikelihood",
    header: "WIN Likelihood",
    width: 150,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <TextCell value={data.winLikelihood} />
    },
  },
  evidenceDueBy: {
    key: "evidenceDueBy",
    header: "Evidence due by",
    width: 150,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <DynamicDateCell timestamp={data.evidenceDueBy} />
    },
  },
  evidenceSubmittedBy: {
    key: "evidenceSubmittedBy",
    header: "Evidence submitted by",
    width: 180,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <TextCell value={data.evidenceSubmittedBy} />
    },
  },
  failureReason: {
    key: "failureReason",
    header: "Failure reason",
    width: 150,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <TextCell value={data.failureReason} />
    },
  },
  failed: {
    key: "failed",
    header: "Failed",
    width: 130,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <DynamicDateCell timestamp={data.failedAt} />
    },
  },
  captureBy: {
    key: "captureBy",
    header: "Capture by",
    width: 130,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <DynamicDateCell timestamp={data.captureBy} />
    },
  },
  store: {
    key: "store",
    header: "Store",
    width: 150,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <TextCell value={data.store} />
    },
  },
  lastUpdated: {
    key: "lastUpdated",
    header: "Last Updated",
    width: 130,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <DynamicDateCell timestamp={data.lastUpdated} />
    },
  },
  declineReason: {
    key: "declineReason",
    header: "Decline Reason",
    width: 150,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <TextCell value={data.declineReason} />
    },
  },
  riskScore: {
    key: "riskScore",
    header: "Risk Score",
    width: 120,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <TextCell value={data.riskScore} />
    },
  },
  merchant: {
    key: "merchant",
    header: "Merchant",
    width: 150,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <TextCell value={data.merchant} />
    },
  },
  program: {
    key: "program",
    header: "Program",
    width: 150,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <TextCell value={data.program} />
    },
  },
  organization: {
    key: "organization",
    header: "Organization",
    width: 150,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <TextCell value={data.organization} />
    },
  },
  bank: {
    key: "bank",
    header: "Bank",
    width: 120,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <TextCell value={data.bank} />
    },
  },
  productPlatform: {
    key: "productPlatform",
    header: "Product Platform",
    width: 150,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <TextCell value={data.productPlatform} />
    },
  },
  processor: {
    key: "processor",
    header: "Processor",
    width: 130,
    render: (payment: Payment) => {
      const data = transformPaymentData(payment)
      return <TextCell value={data.processor} />
    },
  },
}

export const PAYMENT_STATUS_CONFIG = {
  all: ['amount', 'status', 'customer', 'description', 'paymentMethod', 'channel', 'created'],
  succeeded: ['amount', 'status', 'customer', 'description', 'paymentMethod', 'channel', 'created'],
  refunded: ['amount', 'status', 'refundReason', 'customer', 'description', 'paymentMethod', 'created', 'requested', 'refunded', 'refundedBy', 'eligibility'],
  disputed: ['amount', 'status', 'stage', 'disputeReason', 'disputedOn', 'evidenceDueBy', 'winLikelihood', 'customer', 'description', 'paymentMethod', 'created', 'evidenceSubmittedBy'],
  failed: ['amount', 'status', 'failureReason', 'customer', 'description', 'paymentMethod', 'created', 'failed'],
  uncaptured: ['amount', 'status', 'captureBy', 'customer', 'description', 'paymentMethod', 'channel', 'created'],
} as const

export const BANK_PAYMENT_STATUS_CONFIG = {
  all: ['amount', 'status', 'merchant', 'customer', 'description', 'paymentMethod', 'channel', 'created', 'program'],
  succeeded: ['amount', 'status', 'merchant', 'customer', 'description', 'paymentMethod', 'channel', 'created'],
  refunded: ['amount', 'status', 'merchant', 'refundReason', 'customer', 'description', 'paymentMethod', 'created', 'requested', 'refunded', 'refundedBy', 'eligibility'],
  disputed: ['amount', 'status', 'merchant', 'stage', 'disputeReason', 'customer', 'description', 'paymentMethod', 'created', 'refunded', 'disputedOn', 'winLikelihood', 'evidenceDueBy', 'evidenceSubmittedBy'],
  failed: ['amount', 'status', 'merchant', 'failureReason', 'customer', 'description', 'paymentMethod', 'created', 'failed'],
  uncaptured: ['amount', 'status', 'merchant', 'captureBy', 'customer', 'description', 'paymentMethod', 'channel', 'created'],
} as const

export const getPaymentColumns = (status: string = 'all', role?: UserRole): Column<Payment>[] => {
  const config = role === 'bank' ? BANK_PAYMENT_STATUS_CONFIG : PAYMENT_STATUS_CONFIG
  const keys = config[status as keyof typeof config] || config.all
  return keys.map(key => PAYMENT_COLUMNS[key]).filter(Boolean)
}


export const getPaymentColumnConfig = (status: string = 'all', role?: UserRole): ColumnManagementConfig[] => {
  const config = role === 'bank' ? BANK_PAYMENT_STATUS_CONFIG : PAYMENT_STATUS_CONFIG
  const visibleKeys: Set<string> = new Set(config[status as keyof typeof config] || config.all)
  return Object.entries(PAYMENT_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ['amount', 'status'].includes(key),
    order: index
  }))
}